import { useQuery } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { getAuditLogs } from '@/features/audit/api/audit.graphql';
import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { listTenantExpiryAlerts, listTenants } from '@/features/tenants/api/tenants.graphql';
import { ExpiryAlertChip } from '@/features/tenants/components/ExpiryAlertChip';
import { OnboardingProgress } from '@/features/tenants/components/OnboardingProgress';
import { formatDateTime } from '@/features/tenants/module-labels';
import { formatLifecycleDate, statusLabel, type TenantTab } from '@/features/tenants/tenant-labels';

import type { TenantHealthScore } from '../api/metrics.graphql';
import { TREND_LABELS } from '../health-labels';
import styles from '../pages/DashboardPage.module.scss';

const REFRESH_MS = 5 * 60 * 1000;
/** Cuántos tenants estancados se listan; el resto, en el listado filtrado. */
const STALLED_PANEL_TAKE = 5;
const RECENT_DAYS = 7;

export function fichaLink(tenantId: string, tab: TenantTab): string {
  return `/tenants/${tenantId}?tab=${tab}`;
}

interface CaseProps {
  id: string;
  title: string;
  isLoading: boolean;
  error: Error | null;
  empty: string;
  count: number;
  children: ReactNode;
}

/** Un caso de "Requiere atención": carga, error y vacío explícitos. */
function AttentionCase({ id, title, isLoading, error, empty, count, children }: CaseProps): JSX.Element {
  return (
    <section className={styles.attentionCase} aria-labelledby={id}>
      <h3 id={id} className={styles.caseTitle}>
        {title}
        {count > 0 ? <span className={styles.caseCount}>{count}</span> : null}
      </h3>
      {isLoading ? (
        <p className={styles.emptyState}>Cargando…</p>
      ) : error ? (
        <p className={styles.emptyState} role="alert">
          No se pudo cargar: {error.message}
        </p>
      ) : count === 0 ? (
        <p className={styles.emptyState}>{empty}</p>
      ) : (
        children
      )}
    </section>
  );
}

interface Props {
  /** Salud de todos los tenants (la pide el dashboard una sola vez); undefined sin reports.read. */
  health: TenantHealthScore[] | undefined;
  healthLoading: boolean;
  healthError: Error | null;
}

/**
 * OS-1724 — "Requiere atención": pilotos por vencer (OS-1720), mora, onboarding estancado
 * (OS-1718), caída de uso (salud) y cambios recientes de módulos (auditoría, OS-1726). Cada caso usa
 * la consulta existente y respeta la capacidad del operador; cada fila lleva a la pestaña que lo
 * resuelve, con la pestaña en la URL.
 */
export function AttentionPanel({ health, healthLoading, healthError }: Props): JSX.Element {
  const { can } = usePlatformPermissions();
  const canTenants = can('platform.tenants.read');
  const canAudit = can('platform.audit.read');
  const canReports = can('platform.reports.read');

  const expiry = useQuery({
    queryKey: ['platform-tenant-expiry-alerts'],
    queryFn: () => listTenantExpiryAlerts(20),
    refetchInterval: REFRESH_MS,
    enabled: canTenants,
  });
  const pastDue = useQuery({
    queryKey: ['platform-tenants', 'dashboard-past-due'],
    queryFn: () => listTenants({ planStatus: 'past_due', take: 20 }),
    refetchInterval: REFRESH_MS,
    enabled: canTenants,
  });
  const stalled = useQuery({
    queryKey: ['platform-tenants', 'dashboard-stalled'],
    queryFn: () => listTenants({ onboarding: 'STALLED', take: STALLED_PANEL_TAKE }),
    refetchInterval: REFRESH_MS,
    enabled: canTenants,
  });
  const recentSince = new Date(Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const moduleChanges = useQuery({
    queryKey: ['platform-audit-logs', 'dashboard-module-changes'],
    queryFn: () => getAuditLogs({ category: 'modules', from: recentSince }, { take: 10 }),
    refetchInterval: REFRESH_MS,
    enabled: canAudit,
  });

  const drops = (health ?? []).filter((item) => item.usageDrop);
  const expiryItems = expiry.data ?? [];
  const pastDueItems = pastDue.data?.edges ?? [];
  const stalledItems = stalled.data?.edges ?? [];
  const changes = moduleChanges.data?.entries ?? [];

  return (
    <section className={styles.section} aria-labelledby="attention-title">
      <h2 id="attention-title" className={styles.sectionTitle}>
        Requiere atención
      </h2>
      <div className={styles.attentionGrid}>
        {canTenants ? (
          <AttentionCase
            id="expiry-alerts-title"
            title="Pilotos por vencer"
            isLoading={expiry.isLoading}
            error={expiry.error}
            empty="Ningún piloto vence en los próximos 7 días."
            count={expiryItems.length}
          >
            <ul className={styles.alertList}>
              {expiryItems.map((item) => (
                <li key={item.tenantId} className={styles.alertItem}>
                  <Link to={fichaLink(item.tenantId, 'summary')} className={styles.alertTenant}>
                    {item.tenantName}
                  </Link>
                  <span className={styles.alertMeta}>
                    {statusLabel(item.planStatus)} · fin {formatLifecycleDate(item.alert.endsAt)}
                  </span>
                  <ExpiryAlertChip alert={item.alert} />
                </li>
              ))}
            </ul>
          </AttentionCase>
        ) : null}

        {canTenants ? (
          <AttentionCase
            id="past-due-title"
            title="En mora"
            isLoading={pastDue.isLoading}
            error={pastDue.error}
            empty="Ningún tenant en mora."
            count={pastDueItems.length}
          >
            <ul className={styles.alertList}>
              {pastDueItems.map((tenant) => (
                <li key={tenant.id} className={styles.alertItem}>
                  <Link to={fichaLink(tenant.id, 'summary')} className={styles.alertTenant}>
                    {tenant.name}
                  </Link>
                  <span className={styles.alertMeta}>Mantiene el acceso; requiere gestión de cobro.</span>
                </li>
              ))}
            </ul>
          </AttentionCase>
        ) : null}

        {canTenants ? (
          <AttentionCase
            id="stalled-onboarding-title"
            title="Onboarding estancado"
            isLoading={stalled.isLoading}
            error={stalled.error}
            empty="Ningún tenant lleva más de 7 días con menos de la mitad de la activación."
            count={stalledItems.length}
          >
            <ul className={styles.alertList}>
              {stalledItems.map((tenant) => (
                <li key={tenant.id} className={styles.alertItem}>
                  <Link to={fichaLink(tenant.id, 'general')} className={styles.alertTenant}>
                    {tenant.name}
                  </Link>
                  <span className={styles.alertMeta}>
                    {statusLabel(tenant.planStatus)} · alta {formatLifecycleDate(tenant.createdAt)}
                  </span>
                  {tenant.onboarding ? (
                    <OnboardingProgress onboarding={tenant.onboarding} label={`Onboarding de ${tenant.name}`} />
                  ) : null}
                </li>
              ))}
            </ul>
            {stalled.data?.hasNextPage ? (
              <Link to="/tenants?onboarding=STALLED" className={styles.alertTenant}>
                Ver todos los estancados
              </Link>
            ) : null}
          </AttentionCase>
        ) : null}

        {canReports ? (
          <AttentionCase
            id="usage-drop-title"
            title="Caída de uso"
            isLoading={healthLoading}
            error={healthError}
            empty="Ningún tenant con caída de uso (DAU de 7 días bajo la mitad del de 30)."
            count={drops.length}
          >
            <ul className={styles.alertList}>
              {drops.map((item) => (
                <li key={item.tenantId} className={styles.alertItem}>
                  <Link to={fichaLink(item.tenantId, 'summary')} className={styles.alertTenant}>
                    {item.tenantName}
                  </Link>
                  <span className={styles.alertMeta}>
                    DAU 7 días {item.avgDau7 ?? '—'} frente a {item.avgDau ?? '—'} en 30 días
                    {item.trend ? ` · ${TREND_LABELS[item.trend]}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </AttentionCase>
        ) : null}

        {canAudit ? (
          <AttentionCase
            id="module-changes-title"
            title="Cambios recientes de módulos"
            isLoading={moduleChanges.isLoading}
            error={moduleChanges.error}
            empty={`Sin cambios de módulos visibles para ti en los últimos ${RECENT_DAYS} días.`}
            count={changes.length}
          >
            <ul className={styles.alertList}>
              {changes.map((entry) => (
                <li key={entry.id} className={styles.alertItem}>
                  {entry.tenantId ? (
                    <Link to={fichaLink(entry.tenantId, 'modules')} className={styles.alertTenant}>
                      {entry.targetLabel ?? entry.tenantId}
                    </Link>
                  ) : (
                    <span className={styles.alertTenant}>{entry.targetLabel ?? '—'}</span>
                  )}
                  <span className={styles.alertMeta}>
                    {entry.actionLabel} · {formatDateTime(entry.createdAt)}
                    {entry.actorEmail ? ` · ${entry.actorEmail}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </AttentionCase>
        ) : null}
      </div>
    </section>
  );
}
